// src/app/shared/directives/number-input.directive.ts
import {
  Directive, ElementRef, forwardRef, HostListener, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges,
} from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { NumberFormatService } from 'src/app/core/services/number-format.service';
import { isExponentialForm, parseNumber, roundToDecimals } from 'src/app/core/services/number-format.util';

/** Numeric input that parses/formats per the user's number-format preset. Keeps the
 *  bound model a JS number (or null) so save payloads are unchanged. Groups on blur;
 *  shows decimal-only while focused; lenient parse (accepts '.' or ','). */
@Directive({
  standalone: true,
  selector: 'input[appNumberInput]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppNumberInputDirective), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => AppNumberInputDirective), multi: true },
  ],
})
export class AppNumberInputDirective implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy {
  @Input('appNumberInput') mode: '' | 'integer' | 'decimal' = 'decimal';
  // Accept null so a nullable bound expression (e.g. a sibling-derived range bound) can be
  // passed as `[min]="x"` without a `?? undefined` dance; validate() already treats
  // null/'' as "unset".
  @Input() min?: number | string | null;
  @Input() max?: number | string | null;
  @Input() maxDecimals?: number | string;

  private value: number | null = null;
  // True when the field holds non-empty text that does NOT parse to a number (e.g. 'abc',
  // a pasted '€ 12,50'). On type="number" the browser rejected such input outright; on
  // type="text" it reaches us as model=null, so we flag it and surface a `number` validation
  // error rather than letting the field read as valid-but-empty and silently drop the value.
  private invalidText = false;
  private focused = false;
  private sub?: Subscription;
  private onChange: (v: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    private numberFormat: NumberFormatService,
  ) {}

  private get resolvedMode(): 'integer' | 'decimal' {
    return this.mode === 'integer' ? 'integer' : 'decimal';
  }

  ngOnInit(): void {
    this.renderer.setAttribute(
      this.el.nativeElement, 'inputmode', this.resolvedMode === 'integer' ? 'numeric' : 'decimal',
    );
    this.sub = this.numberFormat.numberFormat$.subscribe(() => {
      // Don't re-render while the field holds unparseable text (invalidText): repainting would
      // wipe what the user typed and leave an "invalid number" error on an empty-looking box.
      if (!this.focused && !this.invalidText) this.render();
    });
  }

  /** A bound [min]/[max] often depends on a sibling control (e.g. a price range where
   *  one bound is the other field's value ± epsilon). The native MinValidator/MaxValidator
   *  this directive replaced re-validated on such changes; without re-notifying the form,
   *  the dependent control's error would go stale. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['min'] || changes['max'] || changes['maxDecimals'] || changes['mode']) {
      this.onValidatorChange();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  writeValue(v: number | string | null): void {
    // Treat '' (the common reactive-form initial value) as empty, not 0 — `Number('')`
    // is 0, which would render "0" in a blank required field and let it pass validation.
    // For a STRING value, parse leniently (as typed input is) rather than with strict
    // Number(): a control patched with a formatted/locale string ('1.234,56') would become
    // NaN → null under Number(), silently blanking a field that carried a real value.
    // NB: writeValue sets the display model but never emits onChange, so for a *parseable* string
    // the control's own value stays that raw string and would be saved verbatim. This is
    // latent-only: every production binding is typed `number` and galactus-core serializes numeric
    // fields as JSON numbers (verified — see number-format.service formatNumber), so no string is
    // ever patched in. The string branch here is defensive; keep it, but don't rely on it coercing
    // the model.
    let parsed: number | null;
    // A non-empty STRING that doesn't parse (e.g. a control patched with 'abc' or '€ 12,50')
    // is invalid input, not "empty": without flagging it, writeValue would blank the box and
    // validate() would read the field as valid-but-empty while the un-parsed string stayed in
    // the model and got saved verbatim. Flag it like onInput does so validate() surfaces a
    // `number` error, and keep the raw text visible so the error points at what was in the model.
    let unparseableText = false;
    if (v == null || v === '') {
      parsed = null;
    } else if (typeof v === 'number') {
      parsed = isNaN(v) ? null : v;
    } else {
      parsed = parseNumber(String(v), this.resolvedMode, this.numberFormat.currentSeparators);
      unparseableText = this.isInvalidText(String(v), parsed);
    }
    // A programmatic write replaces whatever the user had typed, so prior invalid-text state is
    // superseded by whatever THIS write is (valid, empty, or itself unparseable — see above).
    const wasInvalid = this.invalidText;
    this.invalidText = unparseableText;
    // Display the value EXACTLY as stored — do not clamp to maxDecimals here. Clamping the
    // display while leaving the model untouched (writeValue never emits) made the box show a
    // rounded number that differed from what was saved: a backend value of 2.999 in a
    // maxDecimals=2 field rendered "3,00" while the control kept — and re-saved — 2.999.
    // renderDigitsInfo echoes the model's own precision, so display == model. Grouping a
    // loaded value to fewer decimals is the user's edit to make (onInput rounds), not a
    // silent rewrite of data on load.
    this.value = parsed;
    // Re-render even while focused when the value is cleared externally (e.g. a
    // validator handler resets the bound model to undefined), so the box doesn't keep
    // showing the now-invalid text; a non-null external change still preserves typing.
    // (Deliberate tradeoff — see the M1 spec: an external clamp while focused, e.g. the
    // discount cap in tab-pagamento, is reflected on blur, not mid-edit; the MODEL always
    // holds the corrected value, so save is never wrong — only the transient focused text.)
    if (this.invalidText) {
      // Show the offending text (unless mid-edit) so the surfaced `number` error is legible.
      if (!this.focused) this.setText(String(v));
    } else if (!this.focused || this.value == null || wasInvalid) {
      // `wasInvalid`: the box currently shows unparseable text ('abc') that a prior write flagged.
      // A valid write clears invalidText, but the normal focused guard would skip render() and
      // leave 'abc' on screen while validate() now reports valid and the model holds the new
      // number — display, validity, and model all disagreeing until blur. There is nothing worth
      // preserving in invalid text, so repaint it even while focused.
      this.render();
    }
  }

  registerOnChange(fn: (v: number | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }
  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('focus')
  onFocus(): void {
    this.focused = true;
    // Keep unparseable text (invalidText) exactly as typed so the user can edit/correct it —
    // clearing it here would strand the "invalid number" error on an empty box.
    if (this.invalidText) return;
    if (this.value == null) {
      this.setText('');
    } else {
      const dec = this.numberFormat.currentSeparators.decimal;
      this.setText(this.plainString(this.value).replace('.', dec));
    }
  }

  /** Decimal string with no grouping and no exponential notation. First strips binary-float
   *  representation noise (e.g. `0.1 + 0.2 = 0.30000000000000004`) to the value's clean
   *  decimal form, so the box never shows a 17-digit noise tail and renderDigitsInfo doesn't
   *  derive a bogus fraction length from it (15 significant digits is the safe round-trip
   *  precision for a double). `String(n)` yields exponential for very small/large magnitudes
   *  (e.g. `5e-7`), which would render as garbage and re-parse wrongly on blur; expand it via
   *  toFixed in that case.
   *
   *  toFixed only expands the SMALL end. Per spec it returns `ToString(x)` for `|x| >= 1e21`, so
   *  `(1e21).toFixed(20)` is still `'1e+21'` and such a value renders (and re-parses as invalid)
   *  in exponential form. Deliberately not handled: 1e21 is ~18 orders of magnitude past any
   *  price or quantity this app edits, and the alternative (BigInt/manual expansion) would print a
   *  22-digit box nobody wants. Documented so the fallback isn't mistaken for total coverage. */
  private plainString(n: number): string {
    const clean = Number.isFinite(n) ? parseFloat(n.toPrecision(15)) : n;
    return isExponentialForm(clean) ? clean.toFixed(20).replace(/\.?0+$/, '') : String(clean);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    // Round to maxDecimals as the user types so the MODEL always honours the field's
    // precision contract. This makes every commit gesture consistent: submitting via Enter
    // (which doesn't blur the input) saves the same value a blur would, instead of persisting
    // an over-precise number that only got rounded on blur. The visible text is left exactly
    // as typed (no caret-jumping rewrite mid-edit); blur re-renders it to match the model.
    const raw = (event.target as HTMLInputElement).value;
    const parsed = this.parse(raw);
    // Non-empty text that doesn't parse is invalid input (not just "empty"): flag it so validate()
    // reports a `number` error instead of the field passing as valid-but-empty. isInvalidText
    // excludes transient in-progress typing states (a lone sign or decimal separator on the way to
    // a real number) — see its doc; shared with writeValue so the classification can't drift.
    this.invalidText = this.isInvalidText(raw, parsed);
    this.value = this.clampDecimals(parsed);
    this.onChange(this.value);
  }

  @HostListener('blur')
  onBlur(): void {
    this.focused = false;
    this.onTouched();
    // No reconciliation needed: onInput already keeps the model rounded to maxDecimals, so a
    // bare focus→blur (no input event) leaves the model untouched and never emits — matching
    // a native number input, which doesn't dirty on focus→blur. render() regroups the text.
    // But when the field holds unparseable text, leave it visible so the "invalid number" error
    // corresponds to what the user actually typed, instead of blanking the box (value is null)
    // while keeping the error — an empty field that reads as invalid with no hint why.
    if (!this.invalidText) this.render();
  }

  /** Parse against the user's active separators so a 3+ digit fraction (e.g. '12,505')
   *  is read as a decimal rather than mistaken for grouping. */
  private parse(raw: string): number | null {
    return parseNumber(raw, this.resolvedMode, this.numberFormat.currentSeparators);
  }

  /** Whether `raw` is genuinely invalid input (a committed non-number) rather than empty or a
   *  transient in-progress typing state. A lone sign ('-'/'+'), a lone decimal separator (',' or
   *  '.'), or sign+separator ('-,') are all on the way to a valid number: parse() returns null for
   *  each, but flagging them would wipe a prior value AND flash a `number` error mid-entry until
   *  the next digit lands (e.g. typing ',' first on the way to ',5' = 0.5). Native number inputs
   *  don't error on these either. Shared by onInput and writeValue so the two can't drift — the
   *  invalidText flag they set is load-bearing for surfacing the `number` validation error. */
  private isInvalidText(raw: string, parsed: number | null): boolean {
    if (parsed != null) return false;
    const trimmed = raw.trim();
    if (trimmed === '') return false;
    const body = trimmed.replace(/^[-+]/, '');
    const partial = body === '' || body === '.' || body === ',';
    return !partial;
  }

  /** DECIMAL mode only: round a parsed value to `maxDecimals` fraction digits, half away
   *  from zero. No-op when maxDecimals is unset/empty/non-integer/negative or mode is integer.
   *  The non-integer/negative guard matters: roundToDecimals feeds `d` into an exponent string
   *  (`${abs}e${d}`), and a fractional or absurd exponent (e.g. maxDecimals="2.5") produces NaN,
   *  which would silently commit NaN to the model — so a misconfigured binding is ignored, not
   *  corrupting. */
  private clampDecimals(n: number | null): number | null {
    if (n == null) return n;
    const d = this.numAttr(this.maxDecimals);
    if (d == null || !Number.isInteger(d) || d < 0 || this.resolvedMode === 'integer') return n;
    return roundToDecimals(n, d);
  }

  /** Coerce an optional numeric @Input (number | string | null) to a number, treating
   *  null/undefined/'' as "unset" (undefined). Centralises the guard shared by maxDecimals,
   *  min and max so it can't drift — a bare Number('') is 0, which would silently turn an
   *  unset bound into a real constraint (or a maxDecimals of 0). */
  private numAttr(v: number | string | null | undefined): number | undefined {
    return v != null && v !== '' ? Number(v) : undefined;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    // The field holds text that isn't a number. type="number" rejected this at entry; on
    // type="text" the raw text never reaches the model (it lands as null), so without this
    // the field would report valid-but-empty and the form would submit with the value
    // silently dropped. Surface it as a `number` error instead.
    if (this.invalidText) return { number: true };
    const v = control.value;
    if (v == null || v === '') return null;
    const num = typeof v === 'number' ? v : this.parse(String(v));
    if (num == null || isNaN(num)) return null;
    // Integer mode: typed input is already truncated by parseNumber('integer'), so the only way a
    // fractional value reaches here is an external write (a backend field patched with 2.7 into an
    // integer control — writeValue keeps the model exactly as stored, it does not silently truncate
    // the display). Surface it as invalid rather than letting the form submit a fractional value
    // through a field contracted to hold integers. Reuses the `number` error/i18n ('invalid number').
    if (this.resolvedMode === 'integer' && !Number.isInteger(num)) return { number: true };
    // Ignore a non-numeric bound (Number(...) → NaN): every comparison against NaN is false, so
    // a garbage [min]/[max] would silently DISABLE the constraint instead of being treated as
    // unset. `isNaN` guards that — a bad bound is a no-op, not a hole in validation.
    const min = this.numAttr(this.min);
    const max = this.numAttr(this.max);
    // Delegate the bound checks to Angular's own Validators.min/max rather than hand-rolling the
    // comparison + error object: they produce exactly the { min: { min, actual } } / { max: {…} }
    // shape consumers expect and can't drift from the framework's contract. Feed them the
    // PRESET-PARSED `num` (via a lightweight probe) so a locale-formatted string control value is
    // still read through the active separators, which Validators.* alone would not do.
    const probe = { value: num } as AbstractControl;
    const errors: ValidationErrors = {
      ...(min != null && !isNaN(min) ? Validators.min(min)(probe) : null),
      ...(max != null && !isNaN(max) ? Validators.max(max)(probe) : null),
    };
    return Object.keys(errors).length ? errors : null;
  }

  private render(): void {
    if (this.value == null) {
      this.setText('');
      return;
    }
    this.setText(this.numberFormat.formatNumber(this.value, this.renderDigitsInfo(this.value)) ?? '');
  }

  /** DigitsInfo for the blurred display. Echoes the model's OWN precision so the box mirrors
   *  exactly what is stored: with no digitsInfo, Angular's it-locale default caps the fraction
   *  at 3 digits, which would silently round the *displayed* value of a higher-precision model
   *  (the bound number is unchanged) and diverge from the full-precision text shown on focus.
   *  Derive the fraction length from `plainString` (already free of grouping/exponential).
   *  This applies to integer mode too: typed input is already truncated to an integer, but a
   *  pre-set fractional bound value (e.g. a backend field that shouldn't be fractional) must
   *  still show its real value — forcing `1.0-0` there would round the display (2.7 → "3")
   *  while the model kept 2.7, so display != model. A genuine integer has frac 0 → "1.0-0". */
  private renderDigitsInfo(n: number): string {
    const frac = this.plainString(n).split('.')[1]?.length ?? 0;
    return `1.0-${frac}`;
  }

  private setText(text: string): void {
    this.renderer.setProperty(this.el.nativeElement, 'value', text);
  }
}
